import React from 'react';
import { KEY_LAYOUT_SPECS } from '../catalog/layout';
import { KEY_STATE } from '../catalog/keyState';
import { STATE_STATUS, needsWrite } from '../catalog/status';
import type { StateStatus } from '../catalog/status';
import { layoutOf, keyNumber, loadsFor } from '../model';
import type { Keypad, KeypadKey } from '../model';
import type { KeyStateReport } from '../stored';
import { reportFor } from '../stored';

export interface KeypadCanvasProps {
  readonly keypad: Keypad;
  readonly reports: readonly KeyStateReport[];
  readonly selectedKeyId: string | null;
  readonly onSelectKey: (key: KeypadKey) => void;
}

/** The dot on a key: what both of its states add up to. */
const keyTone = (reports: readonly KeyStateReport[], key: KeypadKey): string => {
  const statuses = [KEY_STATE.PRESS, KEY_STATE.RELEASE]
    .map((state) => reportFor(reports, key.id, state)?.status)
    .filter((status): status is StateStatus => status !== undefined);

  if (statuses.includes(STATE_STATUS.DIFFERS)) return 'bg-[#C0392B]';
  if (statuses.includes(STATE_STATUS.UNREADABLE)) return 'bg-[#D4A054]';
  if (statuses.some(needsWrite)) return 'bg-[#D4A054]';
  if (statuses.includes(STATE_STATUS.MATCHES)) return 'bg-[#2E7D32]';
  return 'bg-[#CCCCCC]';
};

/**
 * The face of the panel, laid out as the engraving sheet has it.
 *
 * Rows and columns come from the reference app's layouts, so a technician
 * programming a 4-key panel sees the four keys where their fingers will find
 * them — not in a list. Keys in the same place on the face stay in the same place
 * on screen, which is what makes "the top-left one is wrong" a sentence that
 * means something.
 */
export const KeypadCanvas: React.FC<KeypadCanvasProps> = ({
  keypad,
  reports,
  selectedKeyId,
  onSelectKey,
}) => {
  const spec = KEY_LAYOUT_SPECS[layoutOf(keypad)];

  return (
    <div
      className="grid gap-3 rounded-2xl bg-[#1A1A1A] p-5"
      style={{
        gridTemplateRows: `repeat(${spec.rows}, minmax(0, 1fr))`,
        gridTemplateColumns: `repeat(${spec.cols}, minmax(0, 1fr))`,
      }}
    >
      {spec.cells.map((cell) => {
        const key = keypad.keys.find((entry) => entry.index === cell.index);
        if (key === undefined) return null;
        const isSelected = key.id === selectedKeyId;
        const drives = [KEY_STATE.PRESS, KEY_STATE.RELEASE].some(
          (state) => loadsFor(key, state).length > 0,
        );
        return (
          <button
            key={key.id}
            type="button"
            onClick={() => onSelectKey(key)}
            style={{ gridRow: cell.row, gridColumn: cell.col }}
            className={`flex min-h-[5.5rem] flex-col items-start justify-between rounded-xl border p-3 text-left transition-colors ${
              isSelected
                ? 'border-[#D4A054] bg-[#2A2A2A]'
                : 'border-[#333333] bg-[#242424] hover:border-[#D4A054]/60'
            }`}
          >
            <span className="flex w-full items-center justify-between gap-2">
              <span className="text-xs font-medium text-[#D4A054]">
                {keyNumber(key)}
              </span>
              <span className={`h-2.5 w-2.5 rounded-full ${keyTone(reports, key)}`} />
            </span>
            <span className="text-sm text-white">
              {key.name.trim() === '' ? 'Unnamed key' : key.name}
            </span>
            <span className="text-xs text-[#999999]">
              {drives ? 'Programmed' : 'Nothing mapped'}
            </span>
          </button>
        );
      })}
    </div>
  );
};

export default KeypadCanvas;
